import React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppShell from "../components/AppShell";
import { getFinancialDetail } from "../data/financialContent";
import { ChevronLeft, ExternalLink } from "lucide-react";

/**
 * Render a single Financial Freedom topic. Each section is either:
 *   - { h, body }  → heading + paragraph
 *   - { h, list }  → heading + bulleted list (URLs auto-linked)
 */
function renderListItem(item, key) {
  if (typeof item !== "string") {
    return (
      <li key={key} className="text-foreground/85 leading-relaxed flex items-start gap-2">
        <span className="text-brand mt-1.5">·</span>
        <span className="flex-1">{item}</span>
      </li>
    );
  }
  // Find any inline http(s) URL and link it. Works with " · " (middle-dot)
  // " — " (em-dash), " - " (hyphen), or plain "Label https://..." formats so
  // content authors don't have to remember a specific separator convention.
  const urlMatch = item.match(/(https?:\/\/\S+)/);
  if (urlMatch) {
    const url = urlMatch[1];
    const idx = urlMatch.index || 0;
    // Trim trailing separators from the label half (· — - :)
    const label = item.slice(0, idx).replace(/[\s·—\-:]+$/u, "").trim();
    const tail = item.slice(idx + url.length).trim();
    return (
      <li key={key} className="flex items-start gap-2 text-foreground/85 leading-relaxed">
        <span className="text-brand mt-1.5">·</span>
        <span className="flex-1">
          {label && <span>{label} </span>}
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand hover:underline underline-offset-4 inline-flex items-center gap-1"
            data-testid="fin-resource-link"
          >
            {url} <ExternalLink className="w-3 h-3 opacity-60" />
          </a>
          {tail && <span> {tail}</span>}
        </span>
      </li>
    );
  }
  return (
    <li key={key} className="text-foreground/85 leading-relaxed flex items-start gap-2">
      <span className="text-brand mt-1.5">·</span>
      <span className="flex-1">{item}</span>
    </li>
  );
}

export default function FinancialDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const detail = getFinancialDetail(slug, i18n.language);

  if (!detail) {
    return (
      <AppShell>
        <div className="px-8 pt-16">
          <Link to="/financial" className="text-brand">{t("financial.back_to_financial")}</Link>
          <p className="mt-4">{t("financial.not_found")}</p>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="px-8 pt-16 pb-6" data-testid={`page-financial-${slug}`}>
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-foreground/55 hover:text-brand mb-2">
          <ChevronLeft className="w-4 h-4" /> {t("common.back")}
        </button>
        <p className="font-script text-2xl text-brand">{t("financial.detail_kicker")}</p>
        <h1 className="font-serif text-4xl leading-tight">{detail.title}</h1>
        <p className="mt-4 text-foreground/85 leading-relaxed text-lg">{detail.short}</p>
      </div>

      <div className="px-8 pb-10 space-y-4">
        {detail.sections.map((s, i) => (
          <section
            key={i}
            data-testid={`fin-section-${i}`}
            className="rounded-3xl bg-card border border-border p-6"
          >
            <p className="text-xs uppercase tracking-widest text-foreground/55">{s.h}</p>
            {s.body && (
              <p className="mt-3 text-foreground/85 leading-relaxed">{s.body}</p>
            )}
            {Array.isArray(s.list) && (
              <ul className="mt-3 space-y-2">
                {s.list.map((it, j) => renderListItem(it, j))}
              </ul>
            )}
          </section>
        ))}

        <section className="rounded-3xl bg-brand/8 border border-brand/20 p-6">
          <p className="font-script text-xl text-brand">{t("financial.gentle_kicker")}</p>
          <p className="font-serif italic text-foreground/85 mt-1">{t("financial.gentle_body")}</p>
        </section>
      </div>
    </AppShell>
  );
}
